import React, { useCallback, useRef } from 'react';
import { Calendar } from 'primereact/calendar';

/* PrimeReact Calendar za sva polja datuma/vremena u aplikaciji:

   1) MASKA unosa po vrsti polja (kuca se samo cifre, tačke i dvotačke se dopisuju same):
        datum             99.99.9999          (dd.mm.gggg)
        samo vreme        99:99               (hh:mm, 24h)
        datum i vreme     99.99.9999 99:99    (dd.mm.gggg hh:mm)
      Maska se isključuje sa mask={false}; podrazumevani format je dd.mm.yy i 24h.

   2) ISPRAVNO KUCANJE: PrimeReact 10 na nedovršen unos javlja onChange(null), a efekat nad `value` tada
      prepiše tekst polja i dok je u fokusu. Ovde se null nastao kucanjem ne prosleđuje (vrednost ostaje
      poslednja ispravna; nevažeći tekst Calendar na blur vraća na nju), a obrisano polje — i prazna maska
      "__.__.____" — potvrđuje se kao null na blur.

   3) PRIRODNO KUCANJE uz masku: ljudi pišu "1.10.2024", "8:30" ili "1.10.24". Maska traži dve cifre, pa bi tačka
      bila ignorisana, cifre bi se pomerile i nepotpun datum bi se na blur tiho vratio na stari (npr. izveštaj ode za
      pogrešan period). Zato: separator posle JEDNE cifre grupe dopisuje vodeću nulu ("1." -> "01."), a godina sa dve
      cifre se na izlazu dopunjuje na četiri ("24" -> "2024"). */

const MASKA = { datum: '99.99.9999', vreme: '99:99', datumVreme: '99.99.9999 99:99' };
const PLACEHOLDER = { datum: 'dd.mm.gggg', vreme: 'hh:mm', datumVreme: 'dd.mm.gggg hh:mm' };
/** tekst bez slotova maske i separatora — prazan znači da korisnik ništa nije uneo */
const bezMaske = (t) => String(t || '').replace(/[_.:\s/-]/g, '');
const SEPARATORI = new Set(['.', ',', '/', '-', ':', ' ']);

/** grupe cifara maske: [{od, do}] (do isključen), npr. '99.99.9999' -> [0-2, 3-5, 6-10] */
const grupeMaske = (m) => {
    const out = [];
    let od = -1;
    for (let i = 0; i <= m.length; i++) {
        const cifra = m[i] === '9';
        if (cifra && od < 0) od = i;
        if (!cifra && od >= 0) { out.push({ od, do: i }); od = -1; }
    }
    return out;
};

/** upis vrednosti u polje tako da je maska i React vide kao kucanje */
const upisi = (input, vrednost, kursor) => {
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setter.call(input, vrednost);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.setSelectionRange(kursor, kursor);
};

export default function DateInput({ onChange, onInput, onBlur, mask, placeholder, dateFormat = 'dd.mm.yy', hourFormat = '24', inputRef, ...props }) {
    const inputting = useRef(false);
    const lastText = useRef(null);
    const pendingNull = useRef(false);
    const slusaoci = useRef(null);

    const vrsta = props.timeOnly ? 'vreme' : props.showTime ? 'datumVreme' : 'datum';
    const maska = mask === false ? undefined : (mask || MASKA[vrsta]);

    /* osluškivanje na samom polju (pre maske i React-a): vodeća nula na separator, dopuna godine na izlazu */
    const vezi = useCallback((el) => {
        if (typeof inputRef === 'function') inputRef(el); else if (inputRef) inputRef.current = el;
        if (slusaoci.current) { slusaoci.current.el.removeEventListener('keydown', slusaoci.current.kd, true); slusaoci.current.el.removeEventListener('blur', slusaoci.current.bl, true); slusaoci.current = null; }
        if (!el || !maska) return;
        const grupe = grupeMaske(maska);
        const kd = (e) => {
            if (!SEPARATORI.has(e.key) || e.ctrlKey || e.metaKey || e.altKey) return;
            const v = el.value, k = el.selectionStart;
            const g = grupe.find((x) => k > x.od && k < x.do);
            /* tačno jedna cifra na početku grupe (npr. "1_.__.____", kursor iza nje) -> "01" */
            if (!g || g.do - g.od !== 2 || !/\d/.test(v[g.od]) || v[g.od + 1] !== '_' || k !== g.od + 1) return;
            e.preventDefault();
            const novo = `${v.slice(0, g.od)}0${v[g.od]}${v.slice(g.do)}`;
            upisi(el, novo, Math.min(novo.length, g.do + 1));
        };
        const bl = () => {
            const v = el.value;
            const gGod = grupe.find((x) => x.do - x.od === 4);
            if (!gGod) return;
            const god = v.slice(gGod.od, gGod.do);
            if (/^\d\d__$/.test(god)) upisi(el, `${v.slice(0, gGod.od)}20${god.slice(0, 2)}${v.slice(gGod.do)}`, gGod.do);
        };
        el.addEventListener('keydown', kd, true);
        el.addEventListener('blur', bl, true);
        slusaoci.current = { el, kd, bl };
    }, [maska, inputRef]);

    return (
        <Calendar
            {...props}
            inputRef={vezi}
            dateFormat={dateFormat}
            hourFormat={hourFormat}
            mask={maska}
            placeholder={placeholder || PLACEHOLDER[vrsta]}
            onInput={(e) => {
                /* onChange iz kucanja stiže sinhrono u istom handleru — zastavica važi samo do kraja tog poziva */
                inputting.current = true;
                /* maska na blur prvo obriše nedovršen tekst pa javi blur — pamti se tekst iz kucanja */
                if (!e || e.type !== 'blur') lastText.current = e && e.target ? e.target.value : null;
                queueMicrotask(() => { inputting.current = false; });
                if (onInput) onInput(e);
            }}
            onChange={(e) => {
                if (inputting.current && e.value == null) { pendingNull.current = true; return; }
                pendingNull.current = false;
                if (onChange) onChange(e);
            }}
            onBlur={(e) => {
                if (pendingNull.current) {
                    pendingNull.current = false;
                    if (!bezMaske(lastText.current) && onChange) {
                        onChange({ originalEvent: e, value: null, target: { name: props.name, id: props.inputId, value: null } });
                    }
                }
                if (onBlur) onBlur(e);
            }}
        />
    );
}
